import { useNotUsedSumTagKeysWithWorkbookIdAndTableId } from "@/components/table-preview/table/hook";
import { cn } from "@/lib/utils";
import { SquareFunction } from "lucide-react";
import queries from "@/queries";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import ContentBox from "../common/content-box";
import { XBRLPreview } from "./xbrl/xbrl-preview";

const AutoSumBadge = ({ useAutoSum }: { useAutoSum: boolean }) => (
  <span
    className={cn(
      "flex items-center gap-[4px] caption-2-medium px-2 py-1 rounded-md",
      useAutoSum
        ? "text-blue-600 bg-blue-50"
        : "text-neutral-500 bg-neutral-100",
    )}
  >
    <SquareFunction className="w-3 h-3" />
    합계 자동계산 {useAutoSum ? "사용" : "미사용"}
  </span>
);

const XBRLContent = ({
  tableId,
  categoryPreLabel,
}: {
  tableId: string;
  categoryPreLabel: string;
}) => {
  const { workbookId } = useParams();

  const { data: notUsedSumTagKeysData } =
    useNotUsedSumTagKeysWithWorkbookIdAndTableId(workbookId!, tableId!);

  const {
    error,
    data: tablePreview,
    isLoading,
  } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId!)
      ._ctx.editablePreview()
      ._ctx.withQueryParam(notUsedSumTagKeysData?.notUsedSumTagKeys ?? []),
    enabled: !!workbookId && !!tableId && !!notUsedSumTagKeysData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  if (isLoading) return <span className="loading-spinner" />;

  return (
    <ContentBox className="pb-5 items-start gap-2 self-stretch min-w-0 overflow-x-auto mt-5">
      <div className="w-full flex items-center justify-between gap-5">
        <span className="title-3-bold text-primary">{categoryPreLabel}</span>
      </div>
      {tablePreview?.prior.tables.map((_, index) => (
        <Fragment key={index}>
          <AutoSumBadge
            useAutoSum={
              tablePreview?.current.tables[index]?.useAutoSum ?? true
            }
          />
          <XBRLPreview
            isError={!!error}
            tablePreview={tablePreview?.current.tables[index]}
            period="CURRENT"
          />
          <XBRLPreview
            isError={!!error}
            tablePreview={tablePreview?.prior.tables[index]}
            period="PRIOR"
          />
        </Fragment>
      ))}
    </ContentBox>
  );
};

export default XBRLContent;
